import { useContext, useEffect } from "react";
import { DowntimeDisplayUnitsDropdown } from "../../../components/DowntimeDisplayUnitsDropdown/DowntimeDisplayUnitsDropdown.view";
import { DowntimeCarousel } from "./DowntimeCarousel/DowntimeCarousel.view";
import { LogoutButton } from "../../../components/LogoutButton";
import { HMIContext } from "../../../contexts/HMIContext/HMIContext";
import { HMICachedDataContext } from "../../../contexts/HMICachedDataContext/HMICachedDataContext";
import { UserContext } from "../../../contexts/UserContext/UserContext";
import { MachineCenterContext } from "../../../contexts/MachineCenterContext/MachineCenterContext";
import { FailureDetails } from "./FailureDetails/FailureDetails.view";
import { ViewDropdown, ViewOptionsEnum } from "../../../components/ViewDropdown/ViewDropdown.view";
import { Grid, Tooltip, Typography } from "@mui/material";
import { useStyles } from "./HMIDashboard.styles";
import Box from "@mui/material/Box";
import { Details } from "./Details/Details.view";
import { FAILUREDETAILSSTEPS, MachineCenterLogTriggersEnum } from "constants/constants";
import { MQTTBrokerContext } from "contexts/MQTTBrokerContext/MQTTBrokerContext";
import { ShiftDropdown } from "components/ShiftDropdown/ShiftDropdown.view";
import DisplaySettingsIcon from "@mui/icons-material/DisplaySettings";
import Fab from "@mui/material/Fab";
import {
    getCurrentFailureDetailsStep,
    getCurrentStartBelowTriggerDowntimeCount,
    getCurrentStartDowntime,
    getMachineCenterIdHMI,
    isPlcDisconnected,
} from "utilities/storageUtilities";
import useMachineCenterLogs from "hooks/useMachineCenterLogs";

export const HMIDashboard = () => {
    const { machineStatus, continueDowntimeDetailsInput } = useContext(MachineCenterContext);
    const { tokenAvailable } = useContext(UserContext);
    const { store } = useContext(HMICachedDataContext);
    const { view, failureDetailsStep } = useContext(HMIContext);
    const classes = useStyles();
    const { handleOpenMQTTSettingDialog } = useContext(MQTTBrokerContext);
    const { handleShift, shift, failureType, failureCode, functionalLocation, area, notes } = useContext(HMIContext);
    const machineCenter = getMachineCenterIdHMI();
    const { triggerMachineCenterLogs } = useMachineCenterLogs();
    const currentStartDowntime = getCurrentStartDowntime();
    const currentStartBelowTriggerDowntimeCount = getCurrentStartBelowTriggerDowntimeCount();
    const currentFailureDetailsStepFromLocalStorage = getCurrentFailureDetailsStep();

    useEffect(() => {
        if (process.env.REACT_APP_LOGS_ENABLED) {
            const plcDisconnected = isPlcDisconnected();
            const triggerMachineCenterLog = () => {
                const message = {
                    currentFailureDetailsStepFromLocalStorage: currentFailureDetailsStepFromLocalStorage ?? null,
                    currentFailureTypeFromLocalStorage: failureType ?? null,
                    currentFailureCodeFromLocalStorage: failureCode ?? null,
                    currentFunctionalLocationFromLocalStorage: functionalLocation ?? null,
                    currentAreaFromLocalStorage: area ?? null,
                    currentNotesFromLocalStorage: notes ?? null,
                    plcDisconnected: plcDisconnected,
                    machineCenterIdHMI: machineCenter?.machine_center_id ?? null,
                    downtimeCounter: currentStartDowntime ?? null,
                    downtimeCounterBelowTrigger: currentStartBelowTriggerDowntimeCount ?? null,
                };

                triggerMachineCenterLogs(MachineCenterLogTriggersEnum.AT_APP_UPDATE, JSON.stringify(message));
            };

            window.addEventListener("load", triggerMachineCenterLog);
            return () => {
                window.removeEventListener("load", triggerMachineCenterLog);
            };
        }
    }, [
        area,
        failureCode,
        failureType,
        currentFailureDetailsStepFromLocalStorage,
        currentStartBelowTriggerDowntimeCount,
        currentStartDowntime,
        functionalLocation,
        notes,
        machineCenter,
        store.machineCenter?.machine_center_id,
        store.machineCenter?.downtime_method_capture_id,
        triggerMachineCenterLogs,
    ]);

    return (
        <>
            <Box className={classes.container}>
                {((machineStatus && !store.isEventReportEditing && !continueDowntimeDetailsInput) ||
                    failureDetailsStep === FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME) && (
                    <Grid container className={classes.gridContainer}>
                        <Grid
                            item
                            xs={process.env.NODE_ENV.toLowerCase() !== "production" ? 8 : 10}
                            className={classes.dropdowns}
                        >
                            <ViewDropdown bigSize={true} />
                            <ShiftDropdown handleShift={handleShift} shift={shift} bigSize={true} />
                            {view === ViewOptionsEnum.summary && <DowntimeDisplayUnitsDropdown bigSize={true} />}
                        </Grid>
                        {/* this button is for testing purposes only */}
                        {process.env.NODE_ENV.toLowerCase() !== "production" &&
                            window.innerWidth >= 600 &&
                            window.innerWidth <= 1280 && (
                                <Grid item xs={2} className={classes.fab_button}>
                                    <Tooltip
                                        PopperProps={{
                                            className: classes.tooltip,
                                        }}
                                        placement="left"
                                        title={
                                            <Typography className={classes.tooltipTexts}>Open MQTT Settings</Typography>
                                        }
                                    >
                                        <Fab
                                            color="primary"
                                            aria-label="add"
                                            onClick={() => handleOpenMQTTSettingDialog(true)}
                                        >
                                            <DisplaySettingsIcon />
                                        </Fab>
                                    </Tooltip>
                                </Grid>
                            )}
                        {/* end */}

                        {process.env.NODE_ENV.toLowerCase() !== "production" && tokenAvailable && (
                            <Grid item xs={2}>
                                <LogoutButton />
                            </Grid>
                        )}
                        <Grid item xs={12} className={classes.graphs}>
                            {view === ViewOptionsEnum.summary ? <DowntimeCarousel /> : <Details />}
                        </Grid>
                    </Grid>
                )}
                {(!machineStatus || store.isEventReportEditing || continueDowntimeDetailsInput) &&
                    failureDetailsStep !== FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME && <FailureDetails />}
            </Box>
        </>
    );
};
